import { FC } from 'react'
import { Flex } from '@/components/atoms/Grid'
import styled, { css } from 'styled-components'
import { ListItemSeparator } from '@/components/atoms/separator'
import { Body2 } from '@/components/atoms/typography'
import { MenuPosition } from '@/components/organisms/slideMenu'
import { MenuBackIconDefault } from '../atoms/icons/MenuBackIconDefault'
import { MenuCloseIconDefault } from '../atoms/icons/MenuCloseIconDefault'
import { IconButtonReverseMobile } from '@/components/molecules/iconButtons/iconButton'
import { TrashIconDefault } from '@/components/atoms/icons/TrashIconDefault'
import useTranslation from 'next-translate/useTranslation'
import { ResetIconDefault } from '../atoms/icons/ResetIconDefault'
import {
  useActiveFilterDispatch,
  useActiveFiltersCount,
  useFilter
} from '@/providers/filterProvider/filtersProvider'
import { FilterQueryItem } from '@/types/filterQueryItem'
import { ActiveFilterItem } from '@/types/activeFiltersType'

type MobileMenuNavigationBarTitleType = JSX.Element | string

type MobileMenuNavigationBarRedesignProps = {
  onClickBack: () => void
  onClickClose: () => void
  title: MobileMenuNavigationBarTitleType
  menuPosition: MenuPosition
  hideArrow: boolean
  onCancelFiltersDidTap?: () => void
  filterType?: FilterQueryItem | string
  page?: { tagId?: string }
  onResetPendingFilter?: (type: FilterQueryItem | string) => void
  isActionDisabled?: boolean
}

const MobileMenuNavigationBarRedesign: FC<
  MobileMenuNavigationBarRedesignProps & Rest
> = ({
  onClickBack,
  onClickClose,
  hideArrow,
  menuPosition,
  title,
  onCancelFiltersDidTap,
  filterType,
  page,
  onResetPendingFilter,
  isActionDisabled = false,
  ...rest
}) => {
  const { t } = useTranslation('common')
  const { discardAllFilter, toggleFilterAndSetUrl } = useActiveFilterDispatch()
  const activeFilterscount = useActiveFiltersCount()
  const { activeFilters } = useFilter()
  const Title = typeof title === 'string' ? <Body2 m={0}>{title}</Body2> : title
  const tag = activeFilters?.tags.find((tag) => tag?.id === page?.tagId)

  let activeItems: ActiveFilterItem[] = []

  switch (filterType) {
    case FilterQueryItem.COLOR:
      activeItems = activeFilters.colors ?? []
      break
    case FilterQueryItem.GENDER:
      activeItems = activeFilters.genders ?? []
      break
    case FilterQueryItem.SIZE:
      activeItems = activeFilters.sizes ?? []
      break
    case FilterQueryItem.MANUFACTURER:
      activeItems = activeFilters.manufacturers ?? []
      break
    case FilterQueryItem.MATERIAL:
      activeItems = activeFilters.materials ?? []
      break
    case FilterQueryItem.PATTERN:
      activeItems = activeFilters.patterns ?? []
      break
    case FilterQueryItem.CATEGORY:
      activeItems = activeFilters.categories ?? []
      break
    case FilterQueryItem.TAG:
      activeItems = tag?.items
      break
    default:
      activeItems = []
  }
  const onDiscardAllFilter =
    (
      getParamType: FilterQueryItem | string,
      activeItems?: ActiveFilterItem[]
    ) =>
    () => {
      if (isActionDisabled) {
        return
      }

      if (!activeItems || activeItems.length === 0) {
        onResetPendingFilter?.(getParamType)
        return
      }

      activeItems.forEach((activeItem) => {
        toggleFilterAndSetUrl(
          activeItem.id,
          activeItem.englishName,
          getParamType as FilterQueryItem
        )
      })
      onResetPendingFilter?.(getParamType)
      discardAllFilter(getParamType)
    }

  return (
    <Wrapper flexDirection="column" {...rest}>
      <Flex
        justifyContent="flex-start"
        flexDirection="row"
        alignItems="center"
        flexGrow={1}
      >
        {hideArrow ? (
          <StyledMenuBackIcon
            onClick={onClickBack}
            cursorOnHover={true}
            data-testid="mobileMenuBackIcon"
            menuPosition={menuPosition}
          />
        ) : (
          <StyledMenuCloseIconDefault
            onClick={onClickClose}
            cursorOnHover={true}
            data-testid="mobileMenuCloseIcon"
          />
        )}
        <TitleWrap>{Title}</TitleWrap>
        {hideArrow ? (
          <ClearFilterButton
            IconElement={<ResetIconDefault width={12} height={12} />}
            title={t('Category.Filter.resetFilters')}
            onClick={onDiscardAllFilter(
              filterType === FilterQueryItem.TAG
                ? FilterQueryItem.TAG.replace('{tagId}', page.tagId)
                : filterType,
              activeItems
            )}
            isDisabled={isActionDisabled}
          />
        ) : (
          <ClearFilterButton
            IconElement={<TrashIconDefault />}
            title={t('Category.Filter.delete')}
            onClick={activeFilterscount > 0 ? onCancelFiltersDidTap : undefined}
            isDisabled={activeFilterscount === 0}
          />
        )}
      </Flex>
      <ListItemSeparator />
    </Wrapper>
  )
}
export default MobileMenuNavigationBarRedesign

const Wrapper = styled(Flex)`
  background-color: ${(props) => props.theme.colors.navMenuGrey};
  z-index: 10;
`

const TitleWrap = styled.div`
  margin-left: 12px;
`

const StyledMenuBackIcon = styled(MenuBackIconDefault)`
  margin-left: 12px;
  padding: 12px;
  ${(props) =>
    props.menuPosition === MenuPosition.Right && 'transform:rotate(180deg)'};
`

const StyledMenuCloseIconDefault = styled(MenuCloseIconDefault)`
  margin-left: 12px;
  padding: 12px;
`

const ClearFilterButton = styled(IconButtonReverseMobile)<{
  isDisabled?: boolean
}>`
  margin-right: 16px;
  margin-left: auto;

  ${({ isDisabled }) =>
    isDisabled &&
    css`
      opacity: 0.3;
      pointer-events: none;
      cursor: default;
    `}
`
